import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import {
  ArrowLeft, Package, CreditCard, Truck, ClipboardCheck, Plane, CheckCircle2,
  Clock, ChevronRight, ChevronLeft, ImageIcon, ExternalLink, LogOut, Loader2, FileDown,
  ZoomIn, BellRing, AlertTriangle, MessageCircle, X, Calculator, Layers, CheckSquare, Square, Pencil, ChevronUp, ChevronDown, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import FreightEstimator from '../components/FreightEstimator';
import StanbicCipsPanel, { orderSubject, batchSubject } from '../components/StanbicCipsPanel';
import OrderEditor from '../components/OrderEditor';
import { getStoredCurrency } from '../lib/currency';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_payment: { label: 'Pending Payment', icon: CreditCard, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', step: 0 },
  domestic_transit: { label: 'Domestic Transit', icon: Truck, color: 'text-blue-600 bg-blue-50 border-blue-200', step: 1 },
  inspection_warehousing: { label: 'Inspection & Warehousing', icon: ClipboardCheck, color: 'text-purple-600 bg-purple-50 border-purple-200', step: 2 },
  international_transit: { label: "Int'l Transit", icon: Plane, color: 'text-indigo-600 bg-indigo-50 border-indigo-200', step: 3 },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', step: 4 },
};

const STEPS = ['Pending Payment', 'Domestic Transit', 'Inspection', "Int'l Transit", 'Completed'];
const photoUrl = (p) => `${API}/api/cars/images/${p.path.replace('rtr-cars/cars/', '')}`;

function QcReviewPanel({ order, onDecision, deciding }) {
  const [comment, setComment] = useState('');
  const [showIssue, setShowIssue] = useState(false);
  const qc = order.qc_review;
  const agent = order.agent_whatsapp || '233544205844';

  if (qc.status === 'approved') {
    return (
      <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid={`qc-approved-${order.id}`}>
        <p className="font-semibold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> You approved these goods on {new Date(qc.decided_at).toLocaleString()}</p>
        {qc.client_comment && <p className="text-xs text-emerald-700 mt-1">Your comment: "{qc.client_comment}"</p>}
        <a href={`https://wa.me/${agent}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-emerald-700 font-medium hover:underline"><MessageCircle className="w-4 h-4" /> Chat with your agent</a>
      </div>
    );
  }
  if (qc.status === 'issue_reported') {
    return (
      <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-4" data-testid={`qc-issue-${order.id}`}>
        <p className="font-semibold text-red-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> You reported an issue on {new Date(qc.decided_at).toLocaleString()}</p>
        <p className="text-xs text-red-700 mt-1">"{qc.client_comment}" — your agent will contact you on WhatsApp.</p>
        <a href={`https://wa.me/${agent}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-red-700 font-medium hover:underline"><MessageCircle className="w-4 h-4" /> Chat with your agent</a>
      </div>
    );
  }
  return (
    <div className="mt-3 bg-amber-50 border-2 border-amber-300 rounded-xl p-4 space-y-3" data-testid={`qc-pending-${order.id}`}>
      <div>
        <p className="font-bold text-amber-900 flex items-center gap-2"><BellRing className="w-5 h-5" /> Your approval is needed</p>
        <p className="text-sm text-amber-800 mt-1">Our warehouse inspected your goods and uploaded {qc.photo_count} photo{qc.photo_count === 1 ? '' : 's'} on {new Date(qc.requested_at).toLocaleDateString()}. Review them above, then approve so we can ship internationally.</p>
        {qc.note && <p className="text-xs text-amber-800 mt-2 bg-white/60 rounded-lg p-2"><strong>Inspector note:</strong> {qc.note}</p>}
      </div>
      {!showIssue ? (
        <>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optional message to your agent..."
            className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid={`qc-comment-${order.id}`} />
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={() => onDecision(order.id, 'approve', comment)} disabled={deciding}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl" data-testid={`qc-approve-btn-${order.id}`}>
              {deciding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Approve & Notify Agent on WhatsApp
            </Button>
            <Button variant="outline" onClick={() => setShowIssue(true)} disabled={deciding}
              className="border-red-300 text-red-700 hover:bg-red-50 rounded-xl" data-testid={`qc-report-issue-btn-${order.id}`}>
              <AlertTriangle className="w-4 h-4 mr-2" /> Report an issue
            </Button>
          </div>
        </>
      ) : (
        <>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Describe the problem you see in the photos (required)..."
            className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm bg-white focus:border-red-500 focus:outline-none" data-testid={`qc-issue-comment-${order.id}`} />
          <div className="flex gap-2">
            <Button onClick={() => onDecision(order.id, 'issue', comment)} disabled={deciding || !comment.trim()}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl" data-testid={`qc-issue-submit-btn-${order.id}`}>
              {deciding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />} Send issue to agent
            </Button>
            <Button variant="outline" onClick={() => setShowIssue(false)} className="rounded-xl" data-testid={`qc-issue-cancel-btn-${order.id}`}>Back</Button>
          </div>
        </>
      )}
    </div>
  );
}

function StatusTimeline({ currentStatus, history = [] }) {
  const currentStep = STATUS_CONFIG[currentStatus]?.step ?? 0;
  const stepKeys = Object.keys(STATUS_CONFIG);
  const reachedAt = (key) => history.find((h) => h.status === key)?.timestamp;
  return (
    <div data-testid="status-timeline">
      {/* Mobile: vertical single-column timeline */}
      <ol className="sm:hidden my-3 space-y-0" data-testid="status-timeline-mobile">
        {STEPS.map((label, i) => {
          const done = i < currentStep, active = i === currentStep, ts = reachedAt(stepKeys[i]);
          return (
            <li key={label} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 flex-shrink-0 ${done || active ? 'bg-orange-600 border-orange-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-400'} ${active ? 'ring-4 ring-orange-100' : ''}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`w-0.5 flex-1 min-h-[22px] ${done ? 'bg-orange-500' : 'bg-gray-200'}`} />}
              </div>
              <div className={`pb-3 ${i === STEPS.length - 1 ? 'pb-0' : ''}`}>
                <p className={`text-sm font-semibold leading-7 ${done || active ? 'text-gray-900' : 'text-gray-400'}`}>{label}{active && <span className="ml-2 text-[10px] uppercase font-bold text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">Current</span>}</p>
                {ts && <p className="text-[11px] text-gray-400 -mt-1">{new Date(ts).toLocaleDateString()}</p>}
              </div>
            </li>
          );
        })}
      </ol>
      {/* Desktop: horizontal stepper */}
      <div className="hidden sm:flex items-center gap-1 w-full my-4">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                i <= currentStep ? 'bg-orange-600 border-orange-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-400'
              }`}>
                {i < currentStep ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-[10px] mt-1 font-medium ${i <= currentStep ? 'text-orange-700' : 'text-gray-400'}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mt-[-16px] ${i < currentStep ? 'bg-orange-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function ClientOrders() {
  const { client, loading: authLoading, authHeaders, logout } = useClientAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [showEstimator, setShowEstimator] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const highlightId = new URLSearchParams(window.location.search).get('highlight');
  const batchParam = new URLSearchParams(window.location.search).get('batch');
  const currency = getStoredCurrency();

  const fetchBatches = async () => {
    try {
      const res = await fetch(`${API}/api/payments/batches/my`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setBatches(data.data);
        if (batchParam && data.data.some((b) => b.id === batchParam)) setExpandedBatch(batchParam);
      }
    } catch { /* non-blocking */ }
  };

  const createBatch = async () => {
    setCreatingBatch(true);
    try {
      const res = await fetch(`${API}/api/payments/batches`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ order_ids: selectedIds }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not create combined payment');
      toast.success(`Combined payment ${data.data.reference} created — one transfer, one receipt`);
      setSelectMode(false); setSelectedIds([]);
      setExpandedBatch(data.data.id);
      await Promise.all([fetchOrders(), fetchBatches()]);
      setTimeout(() => document.querySelector(`[data-testid="batch-panel-${data.data.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } catch (err) { toast.error(err.message); }
    finally { setCreatingBatch(false); }
  };

  const cancelBatch = async (batch) => {
    try {
      const res = await fetch(`${API}/api/payments/batches/${batch.id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not cancel');
      toast.success('Combined payment cancelled — orders can be paid individually again');
      await Promise.all([fetchOrders(), fetchBatches()]);
    } catch (err) { toast.error(err.message); }
  };

  const bookShipment = async (payload) => {
    setBooking(true);
    const waWin = window.open('', '_blank');
    try {
      const res = await fetch(`${API}/api/sourcing/freight-booking`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      setBookingResult(data);
      toast.success(`Booking request #${data.booking_id} sent to your agent`);
      if (waWin) waWin.location.href = data.whatsapp_url; else window.location.href = data.whatsapp_url;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setBooking(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!client) { navigate('/sourcing/auth'); return; }
    fetchOrders();
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/api/orders/my`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setOrders(data.data);
        if (highlightId && data.data.some((o) => o.id === highlightId)) {
          setSelected(highlightId);
          setTimeout(() => document.querySelector(`[data-testid="order-card-${highlightId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
        }
      }
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const cfg = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.pending_payment;
  const pendingApprovals = orders.filter((o) => o.qc_review?.status === 'pending_approval');
  const payable = orders.filter((o) => o.status === 'pending_payment' && !o.payment?.batch_id && o.payment?.status !== 'awaiting_verification');
  const selectedTotal = orders.filter((o) => selectedIds.includes(o.id)).reduce((s, o) => s + (o.total_rmb || 0), 0);
  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleQcDecision = async (orderId, decision, comment) => {
    setDecidingId(orderId);
    const waWin = window.open('', '_blank');
    try {
      const res = await fetch(`${API}/api/orders/${orderId}/${decision === 'approve' ? 'qc-approve' : 'qc-issue'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ note: comment || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data.data : o)));
      toast.success(decision === 'approve' ? 'Approved — opening WhatsApp to your agent' : 'Issue sent — opening WhatsApp to your agent');
      if (waWin) waWin.location.href = data.whatsapp_url; else window.location.href = data.whatsapp_url;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setDecidingId(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-orders-page">
      {/* Nav */}
      <nav className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/sourcing" className="flex items-center gap-2 text-blue-900 font-bold text-sm whitespace-nowrap" data-testid="orders-back-link">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Sourcing </span>Portal
          </Link>
          <div className="flex items-center gap-3 sm:gap-4 whitespace-nowrap">
            <Link to="/sourcing/wishlist" className="text-sm text-gray-600 hover:text-blue-900 flex items-center gap-1" data-testid="orders-nav-wishlist"><Heart className="w-4 h-4" /> Saved</Link>
            <span className="text-sm text-gray-600 hidden sm:inline">Hi, <strong>{client?.name}</strong></span>
            <button onClick={() => { logout(); navigate('/sourcing'); }} className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1" aria-label="Logout" data-testid="client-logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="orders-heading">My Orders{orders.length > 0 && <span className="ml-2 text-base font-semibold text-gray-400" data-testid="orders-count">({orders.length})</span>}</h1>
          <Button onClick={() => { setShowEstimator((v) => !v); setBookingResult(null); }} variant={showEstimator ? 'default' : 'outline'}
            className={`rounded-xl font-bold ${showEstimator ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}`} data-testid="orders-estimator-toggle">
            <Calculator className="w-4 h-4 mr-2" /> {showEstimator ? 'Hide Freight Estimator' : 'Estimate Freight & Book Shipment'}
          </Button>
        </div>

        {showEstimator && (
          <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5" data-testid="orders-estimator-card">
            <p className="text-sm text-gray-600 mb-3">Estimate your freight at our flat rates, then <strong>Book this shipment</strong> to send the estimate to your agent on WhatsApp for confirmation.</p>
            <FreightEstimator currency={currency} onBook={bookShipment} booking={booking} bookingResult={bookingResult} />
          </div>
        )}

        {/* Combined payments (compact cards, expand to pay) */}
        {batches.length > 0 && (
          <div className="space-y-3 mb-6" data-testid="batch-panel-list">
            {batches.map((b) => {
              const open = expandedBatch === b.id;
              const badge = b.status === 'awaiting_verification' ? ['Awaiting verification', 'bg-sky-100 text-sky-800 border-sky-300'] : b.status === 'rejected' ? ['Receipt rejected', 'bg-red-100 text-red-800 border-red-300'] : ['Payment due', 'bg-amber-100 text-amber-800 border-amber-300'];
              return (
                <div key={b.id} className="rounded-xl border-2 border-violet-200 bg-white overflow-hidden" data-testid={`batch-panel-${b.id}`}>
                  <button type="button" onClick={() => setExpandedBatch(open ? null : b.id)} className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-violet-50/60" data-testid={`batch-toggle-${b.id}`}>
                    <Layers className="w-5 h-5 text-violet-700 flex-shrink-0" />
                    <span className="font-semibold text-gray-900 text-sm">Combined payment <span className="font-mono text-violet-800">{b.reference}</span></span>
                    <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${badge[1]}`}>{badge[0]}</span>
                    <span className="text-xs text-gray-500">{b.orders.length} orders · {b.orders.map((o) => `#${o.id}`).join(', ')}</span>
                    <span className="ml-auto flex items-center gap-3"><strong className="text-emerald-700">¥{b.total_rmb.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>{open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}</span>
                  </button>
                  {open && (
                    <div className="p-3 border-t border-violet-100">
                      <StanbicCipsPanel subject={batchSubject(b)} authHeaders={authHeaders}
                        onUpdated={(updated) => { setBatches((prev) => prev.map((x) => (x.id === updated.id ? updated : x))); fetchOrders(); }}
                        onCancel={['open', 'rejected'].includes(b.status) ? () => cancelBatch(b) : undefined} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Multi-order payment bar */}
        {payable.length >= 2 && (
          <div className={`mb-6 rounded-xl border-2 p-4 ${selectMode ? 'border-violet-400 bg-violet-50' : 'border-violet-200 bg-white'}`} data-testid="multi-pay-bar">
            {!selectMode ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center flex-shrink-0"><Layers className="w-5 h-5" /></div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">Pay multiple orders together</p>
                  <p className="text-sm text-gray-600">You have {payable.length} orders awaiting payment. Combine them into one Stanbic transfer with a single reference and one receipt.</p>
                </div>
                <Button onClick={() => setSelectMode(true)} className="bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl" data-testid="multi-pay-start">Select orders</Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <p className="font-bold text-violet-900">Tick the orders you want to pay together</p>
                  <p className="text-sm text-violet-800" data-testid="multi-pay-summary">{selectedIds.length} selected · ¥{selectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSelectedIds(payable.map((o) => o.id))} className="rounded-xl text-xs" data-testid="multi-pay-select-all">Select all</Button>
                  <Button variant="outline" onClick={() => { setSelectMode(false); setSelectedIds([]); }} className="rounded-xl text-xs" data-testid="multi-pay-cancel">Cancel</Button>
                  <Button onClick={createBatch} disabled={selectedIds.length < 2 || creatingBatch} className="bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl" data-testid="multi-pay-create">
                    {creatingBatch ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />} Pay {selectedIds.length || ''} together
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {pendingApprovals.length > 0 && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="qc-notification-banner">
            <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 animate-pulse"><BellRing className="w-5 h-5" /></div>
            <div className="flex-1">
              <p className="font-bold text-amber-900">{pendingApprovals.length} order{pendingApprovals.length === 1 ? '' : 's'} need{pendingApprovals.length === 1 ? 's' : ''} your approval</p>
              <p className="text-sm text-amber-800">Quality-check photos are ready. Review and approve so we can ship your goods.</p>
            </div>
            <button onClick={() => setSelected(pendingApprovals[0].id)} className="text-sm font-bold text-amber-900 underline whitespace-nowrap" data-testid="qc-banner-review-btn">Review now →</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-600" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No orders yet. Start sourcing products!</p>
            <Link to="/sourcing" className="text-orange-600 hover:text-orange-700 font-medium text-sm mt-2 inline-block">Go to Sourcing Portal</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const c = cfg(order.status);
              const Icon = c.icon;
              const isExpanded = selected === order.id;
              const selectable = selectMode && payable.some((p) => p.id === order.id);
              const inBatch = order.status === 'pending_payment' && order.payment?.batch_id && ['batched', 'awaiting_verification', 'rejected'].includes(order.payment?.status);
              return (
                <div key={order.id} className={`bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow ${selectedIds.includes(order.id) ? 'border-violet-400 ring-2 ring-violet-200' : 'border-gray-200'}`} data-testid={`order-card-${order.id}`}>
                  {/* Summary row */}
                  <button onClick={() => (selectable ? toggleSelect(order.id) : setSelected(isExpanded ? null : order.id))} className="w-full p-4 flex items-center gap-4 text-left">
                    {selectable && (
                      <span className="text-violet-600 flex-shrink-0" data-testid={`order-select-${order.id}`}>{selectedIds.includes(order.id) ? <CheckSquare className="w-6 h-6" /> : <Square className="w-6 h-6" />}</span>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${c.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono text-gray-400">#{order.id}</span>
                        <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${c.color}`}>{c.label}</span>
                        {order.payment?.batch_id && order.status === 'pending_payment' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-violet-100 text-violet-800 border-violet-300" data-testid={`batch-badge-${order.id}`}>Combined payment</span>}
                        {order.payment?.status === 'awaiting_verification' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-sky-100 text-sky-800 border-sky-300" data-testid={`payment-badge-${order.id}`}>Awaiting payment verification</span>}
                        {order.payment?.status === 'rejected' && order.status === 'pending_payment' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-red-100 text-red-800 border-red-300" data-testid={`payment-badge-${order.id}`}>Receipt rejected</span>}
                        {order.qc_review?.status === 'pending_approval' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-amber-100 text-amber-800 border-amber-300 animate-pulse" data-testid={`qc-badge-${order.id}`}>Action required</span>}
                        {order.qc_review?.status === 'approved' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-emerald-100 text-emerald-800 border-emerald-300">QC approved</span>}
                        {order.qc_review?.status === 'issue_reported' && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded border bg-red-100 text-red-800 border-red-300">Issue reported</span>}
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{order.product_title}</h3>
                      <p className="text-xs text-gray-500 flex flex-wrap gap-x-2"><span>Qty: {order.quantity}</span><span className="hidden sm:inline">|</span><span>¥{order.price_rmb?.toFixed(2)}/unit</span><span className="hidden sm:inline">|</span><span className="font-semibold text-gray-700">Total: ¥{order.total_rmb?.toFixed(2)}</span></p>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-4">
                      <StatusTimeline currentStatus={order.status} history={order.status_history} />

                      {order.status === 'pending_payment' && !['awaiting_verification', 'verified'].includes(order.payment?.status) && (
                        editingId === order.id ? (
                          <OrderEditor order={order} authHeaders={authHeaders} onClose={() => setEditingId(null)}
                            onSaved={(updated) => { setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o))); setEditingId(null); if (updated.payment?.batch_id) fetchBatches(); }} />
                        ) : (
                          <button type="button" onClick={() => setEditingId(order.id)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3 py-2 rounded-lg" data-testid={`edit-order-btn-${order.id}`}>
                            <Pencil className="w-4 h-4" /> Edit quantity, options or instructions
                          </button>
                        )
                      )}

                      {order.status === 'pending_payment' && (inBatch ? (
                        <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900 flex flex-wrap items-center gap-2" data-testid={`order-in-batch-${order.id}`}>
                          <Layers className="w-4 h-4 text-violet-700" /> This order is part of combined payment <span className="font-mono font-semibold">{order.payment.reference}</span>.
                          <button type="button" onClick={() => { setExpandedBatch(order.payment.batch_id); setTimeout(() => document.querySelector(`[data-testid="batch-panel-${order.payment.batch_id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }} className="underline font-semibold" data-testid={`order-goto-batch-${order.id}`}>Go to payment</button>
                        </div>
                      ) : (
                        <StanbicCipsPanel subject={orderSubject(order)} authHeaders={authHeaders} onUpdated={(updated) => setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)))} />
                      ))}

                      {/* SKU selections */}
                      {order.sku_selections?.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">Selected Options</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {order.sku_selections.map((s, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100">{s.name}: {s.value}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {order.special_instructions && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">Instructions</span>
                          <p className="text-sm text-gray-700 mt-1">{order.special_instructions}</p>
                        </div>
                      )}

                      {/* Inspection photos */}
                      {order.inspection_photos?.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Inspection Photos ({order.inspection_photos.length}) — tap to enlarge</span>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                            {order.inspection_photos.map((p, idx) => (
                              <button type="button" key={p.id} onClick={() => setLightbox({ photos: order.inspection_photos, idx })} className="group relative" data-testid={`qc-photo-${order.id}-${idx}`}>
                                <img
                                  src={photoUrl(p)}
                                  alt="Inspection"
                                  className="w-full aspect-square object-cover rounded-lg border border-gray-200 group-hover:opacity-90"
                                />
                                <ZoomIn className="absolute bottom-1.5 right-1.5 w-4 h-4 text-white drop-shadow" />
                              </button>
                            ))}
                          </div>

                          {/* QC approval */}
                          {order.qc_review && (
                            <QcReviewPanel order={order} onDecision={handleQcDecision} deciding={decidingId === order.id} />
                          )}
                          {/* Download Inspection Report */}
                          <button
                            onClick={() => {
                              const token = localStorage.getItem('client_token');
                              fetch(`${API}/api/orders/${order.id}/inspection-report`, { headers: { Authorization: `Bearer ${token}` } })
                                .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                                .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `RTR-Inspection-${order.id}.pdf`; a.click(); URL.revokeObjectURL(url); })
                                .catch(() => toast.error('Report not available yet'));
                            }}
                            className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 transition-colors"
                            data-testid={`client-download-report-${order.id}`}
                          >
                            <FileDown className="w-4 h-4" /> Download Inspection Report
                          </button>
                        </div>
                      )}

                      {/* Status history */}
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Clock className="w-3 h-3" /> History</span>
                        <div className="mt-2 space-y-1.5">
                          {order.status_history?.map((h, i) => (
                            <div key={i} className="flex flex-wrap sm:flex-nowrap items-start gap-x-2 gap-y-0.5 text-xs text-gray-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0 mt-1.5" />
                              <span className="font-medium">{STATUS_CONFIG[h.status]?.label || h.status}</span>
                              <span className="text-gray-400 sm:ml-auto order-last sm:order-none w-full sm:w-auto pl-3.5 sm:pl-0">{new Date(h.timestamp).toLocaleDateString()}</span>
                              {h.note && <span className="text-gray-400 w-full sm:w-auto pl-3.5 sm:pl-0 sm:flex-1">— {h.note}</span>}
                            </div>
                          ))}
                        </div>
                      </div>

                      {order.product_url && (
                        <a href={order.product_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> View original listing
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" data-testid="qc-lightbox" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10" onClick={() => setLightbox(null)} data-testid="qc-lightbox-close"><X className="w-8 h-8" /></button>
          {lightbox.photos.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-10" data-testid="qc-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, idx: (l.idx - 1 + l.photos.length) % l.photos.length })); }}><ChevronLeft className="w-6 h-6" /></button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-10" data-testid="qc-lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, idx: (l.idx + 1) % l.photos.length })); }}><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
          <div className="max-w-4xl px-16" onClick={(e) => e.stopPropagation()}>
            <img src={photoUrl(lightbox.photos[lightbox.idx])} alt="Inspection" className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl mx-auto" data-testid="qc-lightbox-image" />
            <p className="text-center mt-3 text-white/70 text-sm" data-testid="qc-lightbox-counter">{lightbox.idx + 1} / {lightbox.photos.length} · {new Date(lightbox.photos[lightbox.idx].uploaded_at).toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}
